import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import PageHeader from "components/common/PageHeader";
import Paginations from "components/common/Paginations";
import DataTable from "components/common/table/DataTable";
import TableShimmer from "components/common/TableShimmer";
import { TimeAgoBadge } from "components/common/TimeAgoBadge";
import { usePermission } from "hooks/usePermission";
import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getAuditLogs,
  resetFilters,
  setFilter,
} from "store/slices/loggersSlice";
import {
  AUDIT_EVENT_PERFORMER_ROLES,
  AUDIT_EVENTS_OPTIONS,
  AUDIT_LOG_MODULES,
  ORDER_BY_OPTIONS,
} from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";

const AuditLogs = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const { canAction } = usePermission();
  const { canView } = canAction("audit_logs");

  const {
    auditLogs: items,
    loading,
    isLoading,
    loadingItemId,
    totalPages,
    currentPage,
    totalCount,
    filters: {
      keyword,
      dates,
      status,
      orderBy,
      actionType,
      performedByRole,
      auditLogModule,
    },
  } = useSelector((state) => state.CommonLogs);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
  }, [canView]);
  const getAllData = (page, action) => {
    const { isReset, isResetKeyword } = getActionFlags(action);

    const payload = {
      perPage: 50,
      pageNo: page || 1,
      orderBy: isReset ? -1 : orderBy,
      keyword: isResetKeyword ? "" : keyword || "",
      dateRange: isReset ? {} : getMinMaxDate(dates),
      performedByRole: isReset ? "" : performedByRole,
      action: isReset ? "" : actionType,
      module: isReset ? "" : auditLogModule,
    };

    dispatch(getAuditLogs(payload));
  };

  const handleReset = () => {
    dispatch(resetFilters());
    getAllData(1, { actionType: "RESET" });
  };

  const filtersList = [
    {
      key: "orderBy",
      placeholder: t("Order By"),
      type: "singleSelect",
      options: ORDER_BY_OPTIONS,
    },
    {
      key: "performedByRole",
      placeholder: t("Performed By"),
      type: "singleSelect",
      options: AUDIT_EVENT_PERFORMER_ROLES,
    },
    {
      key: "actionType",
      placeholder: t("Action"),
      type: "singleSelect",
      options: AUDIT_EVENTS_OPTIONS,
    },
    {
      key: "auditLogModule",
      placeholder: t("Module"),
      type: "singleSelect",
      options: AUDIT_LOG_MODULES,
    },
  ];

  const columns = useMemo(
    () => [
      {
        header: t("Timestamp"),
        key: "createdAt",
        render: (value) => (
          <div>
            <TimeAgoBadge date={value} />
            <div className="ltr_text">{formatUTCDate(value)}</div>
          </div>
        ),
      },
      {
        header: t("Action"),
        key: "action",
        render: (value) => (
          <div>
            {value === "DELETE" ? (
              <span className="membership_log_badge badge-failed">
                {value || "-"}
              </span>
            ) : value === "UPDATE" ? (
              <span className="membership_log_badge badge-info">
                {value || "-"}
              </span>
            ) : (
              <span className="membership_log_badge badge-success">{value || "-"}</span>
            )}
          </div>
        ),
      },
      {
        header: t("Action By"),
        key: "performedBy",
        render: (value) => (
          <div className="">
            {value?.role === "system" ? (
              <>
                <p className="role text-capitalize">By {value?.role}</p>
              </>
            ) : (
              <>
                <p className="name">{value?.name || "-"}</p>
                <p className="role">({value?.role || "-"})</p>
              </>
            )}
          </div>
        ),
      },
      {
        header: t("Module"),
        key: "module",
        render: (value) => (
          <div className="">
            <span>{value}</span>
          </div>
        ),
      },
      {
        header: t("Summery"),
        key: "summary",
        render: (value) => (
          <div className="summery">
            <span>{value}</span>
          </div>
        ),
      },
    ],
    [t],
  );
  const renderActions = useCallback(
    (item) => (
      <>
        {canView && (
          <Link to={`/audit-logs/details?id=${item._id}`}>
            <img src={view} width={20} height={20} alt="view" />
          </Link>
        )}
      </>
    ),
    [canView, isLoading, loadingItemId],
  );

  if (!canView) return null;

  return (
    <div className="page-content audit_logs_page">
      <PageHeader title={t("Audit Logs")} />

      <FilterBar
        keyword={keyword}
        setKeyword={(value) => dispatch(setFilter({ key: "keyword", value }))}
        keywordPlaceholder={t("Search ")}
        filters={filtersList}
        selectedFilters={{
          keyword,
          status,
          orderBy,
          performedByRole,
          actionType,
          auditLogModule,
        }}
        onFilterUpdate={(key, value) => dispatch(setFilter({ key, value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isDateRange={true}
        dates={dates}
        setDates={(value) => dispatch(setFilter({ key: "dates", value }))}
      />

      {loading && <TableShimmer rows={7} columns={8} />}

      {!loading && items?.length === 0 && (
        <NoDataFound title={t("No Audit Logs Found")} icon={noPlans} />
      )}
      {!loading && items?.length > 0 && (
        <>
          <DataTable
            columns={columns}
            data={items}
            renderActions={renderActions}
            t={t}
          />

          <Paginations
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={getAllData}
          />
        </>
      )}
    </div>
  );
};

export default AuditLogs;
